/**
 * Client-side draft mode for the Storymap Agent.
 *
 * The AI's generate endpoint returns a full board where existing items keep
 * their ids and new items carry freshly minted ids. Nothing is persisted by the
 * backend — these helpers merge the NEW items into the local board state as
 * `isDraft` cards (dashed styling, no drag/open), and only when the user clicks
 * "Save to board" are they created for real through the normal add endpoints.
 * Discard simply strips them from local state.
 *
 * Every draft item carries a `draftMeta` object with the raw AI fields (name,
 * description, parent AI ids) so persisting never has to re-parse display
 * titles. AI ids are valid Mongo ObjectIds but exist only in the AI response;
 * `persistAgentDrafts` maps them to real ids as parents are created.
 */

import {
    createJourney,
    createRelease,
    createStep,
    createStory,
    extractPersistedId,
} from '../../storyMap.service'
import { DEFAULT_STORY_STATUS } from './utils'

const asDescription = (value) =>
    typeof value === 'string' && value.trim() ? value : ''

const journeyNumber = (index) => index + 1

const stepTitle = (jNum, sNum, name) => `${jNum}.${sNum} ${name || 'Untitled step'}`

const journeyTitle = (jNum, name) => `${jNum}. ${name || 'Untitled journey'}`

const makeDraftStory = (aiStory) => {
    const description = asDescription(aiStory.description)
    return {
        id: String(aiStory._id),
        title: aiStory.name || 'Untitled story',
        stepId: aiStory.stepId ? String(aiStory.stepId) : aiStory.stepId,
        status: DEFAULT_STORY_STATUS,
        matchesFilter: true,
        isDraft: true,
        ...(description
            ? { description, hasDescription: true, showDescriptionIcon: true }
            : {}),
        draftMeta: {
            name: aiStory.name || 'Untitled story',
            description,
            journeyId: String(aiStory.journeyId ?? ''),
            stepId: String(aiStory.stepId ?? ''),
            releaseId: String(aiStory.releaseId ?? ''),
        },
    }
}

const makeDraftStep = (aiStep, jNum, sNum) => {
    const description = asDescription(aiStep.description)
    return {
        id: String(aiStep._id),
        title: stepTitle(jNum, sNum, aiStep.name),
        matchesFilter: true,
        isDraft: true,
        ...(description
            ? { description, hasDescription: true, showDescriptionIcon: true }
            : {}),
        draftMeta: {
            name: aiStep.name || 'Untitled step',
            description,
            journeyId: String(aiStep.journeyId ?? ''),
        },
    }
}

/**
 * Merge the AI-generated board into the current board state, flagging every
 * item whose id the current board does not know as `isDraft`.
 *
 * Returns `{ board, summary }` where summary counts the drafts added
 * ({ journeys, steps, stories }) — or `null` board when nothing new was found.
 */
export const mergeAgentGenerationIntoBoard = (board, aiBoard) => {
    const existingJourneyIds = new Set()
    const existingStepIds = new Set()
    const existingStoryIds = new Set()
    const existingReleaseIds = new Set()

    for (const journey of board?.journeys ?? []) {
        existingJourneyIds.add(String(journey.id))
        for (const step of journey.steps ?? []) {
            existingStepIds.add(String(step.id))
        }
    }
    for (const release of board?.releases ?? []) {
        existingReleaseIds.add(String(release.id))
        for (const story of release.stories ?? []) {
            existingStoryIds.add(String(story.id))
        }
    }

    const summary = { journeys: 0, steps: 0, stories: 0 }
    const journeys = (board?.journeys ?? []).map((journey) => ({
        ...journey,
        steps: [...(journey.steps ?? [])],
    }))
    const releases = (board?.releases ?? []).map((release) => ({
        ...release,
        stories: [...(release.stories ?? [])],
    }))

    // Journeys & steps. The AI board's journey order does not matter here —
    // existing journeys stay where they are, new ones append at the end.
    for (const aiJourney of aiBoard?.journeys ?? []) {
        const aiJourneyId = String(aiJourney?._id ?? '')
        if (!aiJourneyId) continue

        if (existingJourneyIds.has(aiJourneyId)) {
            const target = journeys.find(
                (journey) => String(journey.id) === aiJourneyId
            )
            if (!target) continue
            const jNum = journeyNumber(journeys.indexOf(target))
            for (const aiStep of aiJourney.steps ?? []) {
                const aiStepId = String(aiStep?._id ?? '')
                if (!aiStepId || existingStepIds.has(aiStepId)) continue
                if (aiStep.isDelete) continue
                target.steps.push(
                    makeDraftStep(aiStep, jNum, target.steps.length + 1)
                )
                summary.steps += 1
            }
        } else {
            if (aiJourney.isActive === false) continue
            const jNum = journeyNumber(journeys.length)
            const steps = []
            for (const aiStep of aiJourney.steps ?? []) {
                if (!aiStep?._id || aiStep.isDelete) continue
                steps.push(makeDraftStep(aiStep, jNum, steps.length + 1))
                summary.steps += 1
            }
            journeys.push({
                id: aiJourneyId,
                title: journeyTitle(jNum, aiJourney.name),
                description: asDescription(aiJourney.description),
                matchesFilter: true,
                isDraft: true,
                steps,
                draftMeta: {
                    name: aiJourney.name || 'Untitled journey',
                    description: asDescription(aiJourney.description),
                },
            })
            summary.journeys += 1
        }
    }

    // Stories all live on the AI board's single release. New ones land on the
    // board's first release — or on a draft release when the board has none.
    const aiRelease = (aiBoard?.release ?? aiBoard?.releases ?? [])[0]
    const newStories = (aiRelease?.stories ?? []).filter(
        (story) =>
            story?._id &&
            !story.isDelete &&
            !existingStoryIds.has(String(story._id))
    )

    if (newStories.length > 0) {
        let targetRelease = releases[0]
        if (!targetRelease) {
            targetRelease = {
                id: String(aiRelease?._id ?? 'agent-draft-release'),
                title: aiRelease?.name || 'Release 1',
                description: '',
                start: null,
                end: null,
                dateRange: '',
                isCollapsed: false,
                matchesFilter: true,
                isDraft: true,
                stories: [],
                draftMeta: { name: aiRelease?.name || 'Release 1' },
            }
            releases.push(targetRelease)
        }
        for (const aiStory of newStories) {
            targetRelease.stories.push(makeDraftStory(aiStory))
            summary.stories += 1
        }
    }

    const total = summary.journeys + summary.steps + summary.stories
    if (total === 0) {
        return { board: null, summary }
    }

    return {
        board: { ...board, journeys, releases },
        summary,
    }
}

/** Remove every draft item from the board (the Discard action). */
export const stripAgentDrafts = (board) => ({
    ...board,
    journeys: (board?.journeys ?? [])
        .filter((journey) => !journey.isDraft)
        .map((journey) => ({
            ...journey,
            steps: (journey.steps ?? []).filter((step) => !step.isDraft),
        })),
    releases: (board?.releases ?? [])
        .filter((release) => !release.isDraft)
        .map((release) => ({
            ...release,
            stories: (release.stories ?? []).filter((story) => !story.isDraft),
        })),
})

export const countAgentDrafts = (board) => {
    let journeys = 0
    let steps = 0
    let stories = 0
    for (const journey of board?.journeys ?? []) {
        if (journey.isDraft) journeys += 1
        for (const step of journey.steps ?? []) {
            if (step.isDraft) steps += 1
        }
    }
    for (const release of board?.releases ?? []) {
        for (const story of release.stories ?? []) {
            if (story.isDraft) stories += 1
        }
    }
    return { journeys, steps, stories, total: journeys + steps + stories }
}

/**
 * Persist every draft on the board through the normal add endpoints, in
 * dependency order (release → journeys → steps → stories), translating AI ids
 * to the real ids the backend mints. When a parent fails, its children are
 * skipped rather than created as orphans.
 *
 * Returns { success, created, failed } where created/failed are counts.
 */
export const persistAgentDrafts = async (board, { projectId } = {}) => {
    const storyMapId = String(board?.id ?? '')
    const idMap = new Map()
    const failedParents = new Set()
    const created = { journeys: 0, steps: 0, stories: 0 }
    let failed = 0
    let firstErrorMessage = ''

    const noteFailure = (message, aiId) => {
        failed += 1
        if (aiId) failedParents.add(String(aiId))
        if (!firstErrorMessage && message) firstErrorMessage = message
    }

    // Draft release (only exists when the board had none at merge time).
    for (const release of board?.releases ?? []) {
        if (!release.isDraft) continue
        const result = await createRelease({
            name: release.draftMeta?.name ?? release.title,
            storyMapId,
            projectId,
            index: 0,
        })
        const realId = extractPersistedId(result?.result)
        if (result?.success && realId) {
            idMap.set(String(release.id), realId)
        } else {
            noteFailure(result?.message, release.id)
        }
    }

    for (const journey of board?.journeys ?? []) {
        if (!journey.isDraft) continue
        const result = await createJourney({
            storyMapId,
            name: journey.draftMeta?.name ?? '',
            ...(journey.draftMeta?.description
                ? { description: journey.draftMeta.description }
                : {}),
        })
        const realId = extractPersistedId(result?.result)
        if (result?.success && realId) {
            idMap.set(String(journey.id), realId)
        } else {
            noteFailure(result?.message, journey.id)
        }
    }

    for (const journey of board?.journeys ?? []) {
        for (const step of journey.steps ?? []) {
            if (!step.isDraft) continue
            const journeyAiId = String(journey.id)
            if (failedParents.has(journeyAiId)) {
                noteFailure('', step.id)
                continue
            }
            const journeyId = idMap.get(journeyAiId) ?? journeyAiId
            const result = await createStep({
                storyMapId,
                journeyId,
                name: step.draftMeta?.name ?? '',
                ...(step.draftMeta?.description
                    ? { description: step.draftMeta.description }
                    : {}),
            })
            const realId = extractPersistedId(result?.result)
            if (result?.success && realId) {
                idMap.set(String(step.id), realId)
                created.steps += 1
            } else {
                noteFailure(result?.message, step.id)
            }
        }
        if (journey.isDraft && idMap.has(String(journey.id))) {
            created.journeys += 1
        }
    }

    for (const release of board?.releases ?? []) {
        const releaseAiId = String(release.id)
        const releaseId = idMap.get(releaseAiId) ?? releaseAiId
        const releaseFailed = failedParents.has(releaseAiId)
        for (const story of release.stories ?? []) {
            if (!story.isDraft) continue
            const meta = story.draftMeta ?? {}
            if (
                releaseFailed ||
                failedParents.has(meta.stepId) ||
                failedParents.has(meta.journeyId)
            ) {
                noteFailure('')
                continue
            }
            const result = await createStory({
                storyMapId,
                projectId,
                releaseId,
                stepId: idMap.get(meta.stepId) ?? meta.stepId,
                journeyId: idMap.get(meta.journeyId) ?? meta.journeyId,
                name: meta.name ?? '',
                ...(meta.description
                    ? { description: meta.description }
                    : {}),
            })
            if (result?.success) {
                created.stories += 1
            } else {
                noteFailure(result?.message)
            }
        }
    }

    return {
        success: failed === 0,
        created,
        failed,
        message: firstErrorMessage,
    }
}
